import { useState } from 'react';
import type { JSX } from 'react';

import { SectionBlock } from 'shared/ui';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { useGetCredentialsQuery } from '../api/integrations';
import { formatJson } from '../lib/json';
import { PROVIDER_KEYS } from '../model/integration';
import type { IntegrationCredential } from '../model/integration';
import CredentialFormDialog from './CredentialFormDialog';

interface CredentialsSectionProps {
  /** Provider shown first: the selected integration's */
  initialProvider?: string;
}

// Credentials belong to no project, since several masters can share one: they are listed per provider
const CredentialsSection = ({ initialProvider }: CredentialsSectionProps): JSX.Element => {
  const [provider, setProvider] = useState(initialProvider ?? PROVIDER_KEYS[0]);
  const [editing, setEditing] = useState<IntegrationCredential | 'new' | null>(null);
  const { data: credentials = [], isLoading, error } = useGetCredentialsQuery(provider);

  return (
    <SectionBlock
      title="Credenciales"
      action={
        <div className="flex items-center gap-2">
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger size="sm" className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROVIDER_KEYS.map((key) => (
                <SelectItem key={key} value={key}>
                  {key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => setEditing('new')}>
            Nueva credencial
          </Button>
        </div>
      }
    >
      {isLoading ? <p className="m-0 text-sm text-text-muted">Cargando…</p> : null}
      {error ? (
        <p className="m-0 text-sm text-error">{extractErrorMessage(error, 'No se pudieron cargar las credenciales')}</p>
      ) : null}
      {!isLoading && !error && credentials.length === 0 ? (
        <p className="m-0 text-sm text-text-muted">Sin credenciales para {provider}.</p>
      ) : null}
      {credentials.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Credencial</TableHead>
              <TableHead>Settings</TableHead>
              <TableHead>Secrets definidos</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {credentials.map((credential) => (
              <TableRow key={credential.id} className="align-top">
                <TableCell>
                  {credential.label} <span className="text-xs text-text-muted">#{credential.id}</span>
                </TableCell>
                <TableCell>
                  <pre className="m-0 max-w-96 overflow-auto text-[11px]">{formatJson(credential.settings)}</pre>
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {Object.keys(credential.secrets ?? {}).join(', ') || '—'}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => setEditing(credential)}>
                    Editar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
      {editing ? (
        <CredentialFormDialog
          provider={provider}
          credential={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </SectionBlock>
  );
};

export default CredentialsSection;
